import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '@/api/cliente'
import { fijarSesionDeSoporte, type Sesion } from '@/dominio/sesion/sesion'
import { sesionConsolaActual } from './sesion'
import type { ModoSoporte } from './tipos'
import { abrirSoporte, cerrarSoporte } from './useConsola'

/**
 * Modo soporte: entrar a la cuenta de un contribuyente como él
 * (`docs/09-consola-del-dueno.md` §4).
 *
 * Se abre en **esta misma pestaña**. El portal y la consola guardan su testigo
 * en `sessionStorage`, que es por pestaña: abrir otra obligaría a pasarle el
 * testigo por la URL —que acaba en el historial, en los registros del proxy y
 * en el `Referer`— o a fiarse de que el navegador copie el almacenamiento, que
 * no todos hacen. La consola no se pierde: su testigo sigue en su clave y la
 * franja del portal lleva de vuelta.
 */

/**
 * Lo que cambia de contribuyente tiene que vaciar la caché del portal.
 *
 * Las claves de consulta del portal no llevan el contribuyente —no les hacía
 * falta: el tenant va dentro del testigo—, así que una pestaña que antes vio
 * el portal de otra cuenta tendría en caché **sus** documentos, y los
 * enseñaría bajo la franja del cliente hasta la siguiente recarga. Se borra
 * todo menos lo de la consola.
 */
function vaciarCacheDelPortal(cliente: QueryClient): void {
  cliente.removeQueries({ predicate: (consulta) => consulta.queryKey[0] !== 'consola' })
}

export function useEntrarComoSoporte() {
  const cliente = useQueryClient()

  return async (tenantId: string, modo: ModoSoporte, motivo: string): Promise<void> => {
    const abierta = await abrirSoporte(tenantId, modo, motivo)
    vaciarCacheDelPortal(cliente)
    fijarSesionDeSoporte({
      testigo: abierta.testigo,
      expira: abierta.expira,
      // El correo es el del operador, no el del cliente: es quien está dentro y
      // es lo que la barra superior del portal enseña en el avatar.
      email: sesionConsolaActual()?.operador.email ?? 'soporte',
      sesionId: abierta.sesionId,
      modo: abierta.modo,
      tenant: abierta.tenant,
    })
  }
}

/** Lo que `GET /interna/sesion/actual` añade en modo soporte. */
export interface SoporteEnCurso {
  modo: ModoSoporte
  operador: string
  motivo: string
  expira: string
}

interface SesionActualDelServidor {
  soporte?: SoporteEnCurso | null
}

/**
 * El soporte según el servidor. Es quien manda: la sesión de soporte puede
 * haberse cerrado desde otra pestaña de la consola, y entonces el testigo que
 * guarda esta ya no vale aunque no haya caducado.
 */
export function useSoporteDelServidor(sesion: Sesion | null) {
  return useQuery({
    queryKey: ['sesion-actual', sesion?.activo?.tenant ?? sesion?.despacho?.despacho ?? '', sesion?.expira.getTime() ?? 0],
    queryFn: ({ signal }) => api.obtener<SesionActualDelServidor>('/sesion/actual', { senal: signal }),
    enabled: sesion !== null,
    staleTime: 60_000,
    select: (datos) => datos.soporte ?? null,
  })
}

export function useSalirDelSoporte() {
  const cliente = useQueryClient()

  /**
   * Cierra la sesión en el servidor con el testigo **de la consola**. Si la
   * consola ya caducó, el cierre falla con 401: se informa y se ofrece
   * descartar el testigo igualmente, porque la sesión de soporte caduca sola a
   * su hora y lo que no puede pasar es que el operador se quede atrapado.
   */
  const cerrarEnElServidor = (sesionId: string): Promise<void> => cerrarSoporte(sesionId)

  const olvidarEnEstaPestana = (): void => vaciarCacheDelPortal(cliente)

  return { cerrarEnElServidor, olvidarEnEstaPestana }
}
